import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Users, Medal, GraduationCap } from "lucide-react";
import { api } from "@/lib/api";

const GOLONGAN_COLORS = { Siaga: "#2E8B57", Penggalang: "#C0392B", Penegak: "#E5A93C", Pandega: "#6B5B4E" };

function ChartCard({ title, subtitle, icon: Icon, testid, children }) {
  return (
    <div data-testid={testid} className="bg-white rounded-xl border border-[#E5DFD7] shadow-sm p-5 sm:p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl bg-[#4A2E2B] flex items-center justify-center shrink-0">
          <Icon className="h-5 w-5 text-[#E5A93C]" />
        </div>
        <div>
          <h3 className="font-display text-base md:text-lg font-semibold text-[#2A1F1B] leading-tight">{title}</h3>
          <p className="text-xs text-[#8C827A]">{subtitle}</p>
        </div>
      </div>
      <div className="h-72">{children}</div>
    </div>
  );
}

const axisProps = { tick: { fontSize: 11, fill: "#8C827A" }, axisLine: false, tickLine: false };

export function PublicStats() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/public/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  if (!stats) return null;

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20" data-testid="landing-public-stats">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest font-bold text-[#B85C38] mb-2">Data Terkini</div>
        <h2 className="font-display text-base md:text-lg font-semibold text-[#1F1917]">
          Sebaran Pramuka Kota Depok per Kwartir Ranting
        </h2>
        <p className="text-sm text-[#8C827A] mt-1">
          <span data-testid="public-total-pramuka" className="font-bold text-[#4A2E2B]">{stats.total_pramuka.toLocaleString("id-ID")}</span> anggota terdaftar ·{" "}
          <span data-testid="public-total-garuda" className="font-bold text-[#4A2E2B]">{stats.total_garuda.toLocaleString("id-ID")}</span> Pramuka Garuda ·{" "}
          <span data-testid="public-total-pembina" className="font-bold text-[#4A2E2B]">{stats.total_pembina.toLocaleString("id-ID")}</span> Pembina ·{" "}
          <span data-testid="public-total-pelatih" className="font-bold text-[#4A2E2B]">{stats.total_pelatih.toLocaleString("id-ID")}</span> Pelatih
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <ChartCard title="Jumlah Pramuka per Kwarran" subtitle="Anggota terdaftar di setiap kwartir ranting" icon={Users} testid="chart-pramuka-kwarran">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={stats.per_kwarran} margin={{ top: 5, right: 5, left: -20, bottom: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EFEAE3" vertical={false} />
              <XAxis dataKey="name" {...axisProps} angle={-35} textAnchor="end" interval={0} />
              <YAxis {...axisProps} allowDecimals={false} />
              <Tooltip cursor={{ fill: "#F7F4EF" }} />
              <Bar dataKey="value" name="Pramuka" fill="#4A2E2B" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Jumlah Pembina & Pelatih per Kwarran" subtitle="Anggota dewasa terdaftar di setiap kwartir ranting" icon={GraduationCap} testid="chart-dewasa-kwarran">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={stats.dewasa_per_kwarran} margin={{ top: 5, right: 5, left: -20, bottom: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EFEAE3" vertical={false} />
              <XAxis dataKey="name" {...axisProps} angle={-35} textAnchor="end" interval={0} />
              <YAxis {...axisProps} allowDecimals={false} />
              <Tooltip cursor={{ fill: "#F7F4EF" }} />
              <Legend verticalAlign="top" wrapperStyle={{ fontSize: 12, paddingBottom: 8 }} />
              <Bar dataKey="Pembina" fill="#4A2E2B" radius={[6, 6, 0, 0]} />
              <Bar dataKey="Pelatih" fill="#E5A93C" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <div className="lg:col-span-2">
          <ChartCard title="Pramuka Garuda per Kwarran menurut Jenjang" subtitle="Siaga · Penggalang · Penegak · Pandega" icon={Medal} testid="chart-garuda-kwarran">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.garuda_per_kwarran} margin={{ top: 5, right: 5, left: -20, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EFEAE3" vertical={false} />
                <XAxis dataKey="name" {...axisProps} angle={-35} textAnchor="end" interval={0} />
                <YAxis {...axisProps} allowDecimals={false} />
                <Tooltip cursor={{ fill: "#F7F4EF" }} />
                <Legend verticalAlign="top" wrapperStyle={{ fontSize: 12, paddingBottom: 8 }} />
                {Object.entries(GOLONGAN_COLORS).map(([g, c]) => (
                  <Bar key={g} dataKey={g} stackId="garuda" fill={c} radius={g === "Pandega" ? [6, 6, 0, 0] : 0} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>
    </section>
  );
}
